import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import isEmpty from 'lodash-es/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AtiraDropdown } from '../../../components/AtiraDropdown';
import { AtiraIcon } from '../../../components/AtiraIcon';
import { Button } from '../../../components/Button';
import { CopyButton } from '../../../components/CopyButton';
import { Flex } from '../../../components/Flex';
import { PageHeader } from '../../../components/PageHeader';
import { Spinner } from '../../../components/Spinner';
import { Text } from '../../../components/Text';
import { UserHeader } from '../../../components/UserHeader';
import i18n, { AppLangs } from '../../../i18n';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { useAppSelector } from '../../../redux/store';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { getUserLinkUrl } from '../../../utils/AtiraLink';

const StyledPageHeader = styled(PageHeader)`
  .header-right-component {
    display: none;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    .header-right-component {
      display: flex;
    }
  }
`;

const UserHeaderWrapper = styled(Flex)`
  display: none;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
  }
`;

const InputSection = styled(Flex)`
  background-color: ${({ theme }) => theme.bgCanvas};
  border-radius: ${Rounded.lg};
  align-items: center;
  width: auto;
  gap: ${Spacing.s};
  height: 3rem;
  padding: ${Spacing.s};

  @media (max-width: ${Breakpoints.TABLET}) {
    flex-direction: column;
    align-items: center;
  }
`;

const LinkCreateWrapper = styled(InputSection)`
  cursor: pointer;
  flex-direction: row;
  gap: ${Spacing.l};
  align-items: center;
  padding: ${Spacing.m} ${Spacing.l};

  p {
    line-height: normal;
  }
`;

const FullWidthOnMobile = styled(Flex)`
  width: 100%;
  gap: ${Spacing.s};
  align-items: center;
  justify-content: center;
  margin-inline-end: ${Spacing.s};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: fit-content;
  }
`;

const LinkSection = styled(InputSection)`
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const ActionsButton = styled(Button)`
  background-color: ${({ theme }) => theme.bgCanvas};
  color: ${({ theme }) => theme.fgPrimary};
  padding: 0;
  padding-inline-start: ${Spacing.s};
  margin-inline-start: auto;
`;

const StyledCopyButton = styled(CopyButton)`
  width: 100%;
  padding: ${Spacing.s} 0;
  color: ${({ theme }) => theme.fgPrimary};

  & > div {
    justify-content: flex-start !important;
  }
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.main};
  font-weight: bold;
  white-space: nowrap;
`;

const LinkText = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  direction: ltr;
  max-width: 15rem;
`;

const ArrowIconWrapper = styled(Flex)`
  svg {
    rotate: ${i18n.language === AppLangs.AR ? '180deg' : '0deg'};
  }
`;

const StyledButton = styled(Button)`
  padding: ${Spacing.s} 0;
  color: ${({ theme }) => theme.fgPrimary};

  & > div {
    justify-content: flex-start !important;
  }
`;

export const DashboardHeader: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const atiraLinkLoading = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinkLoading,
  );
  const userLinkUrl = getUserLinkUrl(selectedAtiraLink?.linkName!);

  const linkActionItems = [
    {
      key: 'preview',
      title: '',
      label: (
        <StyledButton
          icon={faEye}
          backgroundColor="transparent"
          onClick={() => window.open(userLinkUrl, '_blank')}
        >
          {t('common.preview')}
        </StyledButton>
      ),
    },
    {
      key: 'copy',
      title: '',
      label: (
        <StyledCopyButton
          copy={userLinkUrl}
          iconSize="1x"
          showText
          open={false}
        />
      ),
    },
  ];

  return (
    <StyledPageHeader
      leftComponent={
        !isEmpty(selectedAtiraLink) ? (
          <FullWidthOnMobile>
            <LinkSection>
              <Title>{t('dashboard.widget.atiraLink.title')}:</Title>
              <LinkText>{userLinkUrl}</LinkText>

              <AtiraDropdown items={linkActionItems} trigger={['click']}>
                <ActionsButton icon={faAngleDown} />
              </AtiraDropdown>
            </LinkSection>
          </FullWidthOnMobile>
        ) : (
          <LinkCreateWrapper onClick={() => navigate('/my-link')}>
            {atiraLinkLoading ? (
              <Spinner />
            ) : (
              <Text color="main" fontSize="l" fontWeight={'bolder'}>
                {t('dashboard.create_link')}
              </Text>
            )}

            <ArrowIconWrapper>
              <AtiraIcon size="2x" icon={faArrowRight} color="main" />
            </ArrowIconWrapper>
          </LinkCreateWrapper>
        )
      }
      rightComponent={
        <UserHeaderWrapper>
          <UserHeader />
        </UserHeaderWrapper>
      }
    />
  );
};
